import { apiFetch, apiJson, apiJsonFormData } from './http';

export type PlaylistRow = {
  id: number;
  name: string;
  is_active?: number;
  channel_count?: number;
  movie_count?: number;
  series_count?: number;
  user_count?: number;
  updated_at?: string | null;
  expiry_date?: string | null;
  sort_order?: number | null;
  url_source?: string | null;
  /** Per-playlist sharing (PHP); inherit=1 uses account defaults from user/client-public-url.php */
  sharing_inherit_account?: number;
  sharing_public_base_url?: string | null;
  sharing_stream_proxy?: number | null;
  epg_server_shift_hours?: number | null;
};

export type PlaylistOwnerAccessData = {
  id: number;
  playlist_id: number;
  name: string;
  username: string;
  access_token: string;
  is_playlist_owner: number;
  effective_public_base_url: string;
  effective_stream_proxy: boolean;
  catchup_available?: boolean;
  catchup_channels?: number;
  sharing_inherited: boolean;
};

export type PlaylistSharingData = {
  sharing_inherit_account: number;
  sharing_public_base_url: string | null;
  sharing_stream_proxy: number | null;
  effective_public_base_url: string;
  effective_stream_proxy: boolean;
  catchup_available?: boolean;
  catchup_channels?: number;
};

export async function fetchPlaylistOwnerAccess(playlistId: number): Promise<PlaylistOwnerAccessData> {
  const path = `/api/playlists/owner-access.php?playlist_id=${encodeURIComponent(String(playlistId))}`;
  const j = await apiJson<{ success?: boolean; data?: PlaylistOwnerAccessData; message?: string }>(
    path,
  );
  if (!('success' in j) && !('data' in j)) {
    throw new Error(`Empty response from ${path}`);
  }
  if (!j.success || !j.data) {
    throw new Error(j.message || 'Owner access response is missing required data');
  }
  return j.data;
}

export async function fetchPlaylistSharing(playlistId: number): Promise<PlaylistSharingData> {
  const res = await apiFetch(
    `/api/playlists/sharing.php?playlist_id=${encodeURIComponent(String(playlistId))}`,
  );
  const j = (await res.json()) as { success?: boolean; data?: PlaylistSharingData; message?: string };
  if (!res.ok || !j.success || !j.data) {
    throw new Error(j.message || `Playlist sharing HTTP ${res.status}`);
  }
  return j.data;
}

export async function updatePlaylistSharing(payload: {
  playlist_id: number;
  sharing_inherit_account: number;
  sharing_public_base_url?: string | null;
  sharing_stream_proxy?: boolean | null;
}): Promise<{ success: boolean; message?: string; data?: PlaylistSharingData }> {
  return apiJson('/api/playlists/sharing.php', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function fetchPlaylistList(): Promise<PlaylistRow[]> {
  const res = await apiFetch('/api/playlists/get.php');
  if (!res.ok) throw new Error(`Playlists HTTP ${res.status}`);
  return res.json() as Promise<PlaylistRow[]>;
}

export async function createPlaylist(body: {
  name: string;
  description?: string;
  url_source?: string;
}): Promise<{ success: boolean; id?: number; message?: string }> {
  return apiJson('/api/playlists/create.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function renamePlaylist(
  playlistId: number,
  newName: string,
): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/playlists/rename.php', {
    method: 'PUT',
    body: JSON.stringify({ playlist_id: playlistId, new_name: newName }),
  });
}

export async function fetchPlaylistById(id: number): Promise<PlaylistRow & { url_source?: string | null }> {
  const res = await apiFetch(`/api/playlists/get.php?id=${id}`);
  if (!res.ok) throw new Error(`Playlist HTTP ${res.status}`);
  return res.json() as Promise<PlaylistRow & { url_source?: string | null }>;
}

export async function updatePlaylist(
  id: number,
  body: { name?: string; url_source?: string | null; epg_server_shift_hours?: number },
): Promise<{ success: boolean; message?: string }> {
  return apiJson(`/api/playlists/update.php?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deletePlaylist(id: number): Promise<{ success: boolean; message?: string }> {
  return apiJson(`/api/playlists/delete.php?id=${id}`, { method: 'DELETE' });
}

export async function duplicatePlaylist(
  playlistId: number,
): Promise<{ success: boolean; id?: number; name?: string; channels_copied?: number; message?: string }> {
  return apiJson('/api/playlists/duplicate.php', {
    method: 'POST',
    body: JSON.stringify({ playlist_id: playlistId }),
  });
}

export type PlaylistResetResult = {
  success: boolean;
  mode?: 'reset_streams' | 'reset_m3u_epg';
  channels_deleted?: number;
  categories_deleted?: number;
  epg_ids_cleared?: number;
  updater_urls_cleared?: number;
  message?: string;
};

export async function resetPlaylistStreams(playlistId: number): Promise<PlaylistResetResult> {
  return apiJson('/api/playlists/reset.php', {
    method: 'POST',
    body: JSON.stringify({ playlist_id: playlistId, mode: 'reset_streams' }),
  });
}

export async function resetPlaylistM3uAndEpg(playlistId: number): Promise<PlaylistResetResult> {
  return apiJson('/api/playlists/reset.php', {
    method: 'POST',
    body: JSON.stringify({ playlist_id: playlistId, mode: 'reset_m3u_epg' }),
  });
}

export type BackupRestoreSelection = {
  playlist_details: boolean;
  channels: boolean;
  movies: boolean;
  tv_series: boolean;
  managed_users: boolean;
  auto_updater: boolean;
};

export async function downloadPlaylistBackup(params: {
  playlistId: number;
  mode: 'private' | 'shareable';
  selection: BackupRestoreSelection;
}): Promise<{ blob: Blob; filename: string }> {
  const qs = new URLSearchParams({
    playlist_id: String(params.playlistId),
    mode: params.mode,
    playlist_details: params.selection.playlist_details ? '1' : '0',
    channels: params.selection.channels ? '1' : '0',
    movies: params.selection.movies ? '1' : '0',
    tv_series: params.selection.tv_series ? '1' : '0',
    managed_users: params.selection.managed_users ? '1' : '0',
    auto_updater: params.selection.auto_updater ? '1' : '0',
  });
  const res = await apiFetch(`/api/playlists/backup.php?${qs.toString()}`);
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `Backup failed (${res.status})`);
  }
  const blob = await res.blob();
  const disp = res.headers.get('Content-Disposition') || '';
  const match = disp.match(/filename="?([^"]+)"?/i);
  return { blob, filename: (match?.[1] || `playlist-backup-${params.playlistId}.json`).trim() };
}

export async function restorePlaylistBackup(params: {
  playlistId?: number | string;
  file: File;
  selection: BackupRestoreSelection;
  previewOnly?: boolean;
  allowConflictsSkip?: boolean;
}): Promise<{ success: boolean; message?: string; summary?: Record<string, number>; warnings?: string[]; has_conflicts?: boolean }> {
  const fd = new FormData();
  const parsedPlaylistId =
    params.playlistId == null
      ? 0
      : Number(
          typeof params.playlistId === 'string'
            ? params.playlistId.trim()
            : params.playlistId,
        );
  if (Number.isFinite(parsedPlaylistId) && parsedPlaylistId > 0) {
    fd.append('playlist_id', String(parsedPlaylistId));
  }
  fd.append('playlist_details', params.selection.playlist_details ? '1' : '0');
  fd.append('channels', params.selection.channels ? '1' : '0');
  fd.append('movies', params.selection.movies ? '1' : '0');
  fd.append('tv_series', params.selection.tv_series ? '1' : '0');
  fd.append('managed_users', params.selection.managed_users ? '1' : '0');
  fd.append('auto_updater', params.selection.auto_updater ? '1' : '0');
  fd.append('preview_only', params.previewOnly ? '1' : '0');
  fd.append('allow_conflicts_skip', params.allowConflictsSkip ? '1' : '0');
  fd.append('backup_file', params.file);
  const res = await apiFetch('/api/playlists/restore.php', { method: 'POST', body: fd });
  const text = await res.text();
  try {
    const json = JSON.parse(text) as { success: boolean; message?: string; summary?: Record<string, number>; warnings?: string[]; has_conflicts?: boolean };
    if (!res.ok) throw new Error(json.message || `Restore failed (${res.status})`);
    return json;
  } catch {
    if (!res.ok) throw new Error(text || `Restore failed (${res.status})`);
    throw new Error('Invalid restore response');
  }
}

/** Kolejność ID playlist użytkownika (sort_order po kolei). */
export async function reorderPlaylists(order: number[]): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/playlists/reorder.php', {
    method: 'POST',
    body: JSON.stringify({ order }),
  });
}

export async function importPlaylistFromUrl(payload: {
  playlist_id: number;
  url: string;
}): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/playlists/import.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Fetch M3U text from URL (server-side curl) without modifying any playlist. */
export async function fetchPlaylistM3uFromUrl(payload: {
  url: string;
}): Promise<{ success: boolean; content?: string; message?: string }> {
  return apiJson('/api/playlists/fetch_m3u.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type MergeImportMode = 'append_all' | 'append_dedup';

export type MergeImportResult = {
  success: boolean;
  added?: number;
  skipped?: number;
  errors?: number;
  parsed?: number;
  total?: number;
  elapsed_ms?: number;
  message?: string;
};

/** Bulk-append channels to a playlist (does not delete existing channels). */
export async function mergeImportChannels(payload: {
  playlist_id: number;
  channels: Record<string, unknown>[];
  import_mode?: MergeImportMode;
  import_category?: boolean;
  import_image?: boolean;
  import_epg?: boolean;
  import_epg_shift?: boolean;
  import_channel_number?: boolean;
}): Promise<MergeImportResult> {
  return apiJson('/api/playlists/merge_import.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

const MERGE_IMPORT_CHUNK_SIZE = 500;

export type MergeImportAttrFlags = {
  import_category?: boolean;
  import_image?: boolean;
  import_epg?: boolean;
  import_epg_shift?: boolean;
  import_channel_number?: boolean;
};

export async function mergeImportChannelsChunked(
  playlistId: number,
  channelPayloads: Record<string, unknown>[],
  onProgress?: (done: number, total: number) => void,
  importMode: MergeImportMode = 'append_dedup',
  attrFlags?: MergeImportAttrFlags,
): Promise<{ added: number; skipped: number; errors: number; parsed: number }> {
  let added = 0;
  let skipped = 0;
  let errors = 0;
  let parsed = 0;
  const total = channelPayloads.length;

  for (let i = 0; i < channelPayloads.length; i += MERGE_IMPORT_CHUNK_SIZE) {
    const chunk = channelPayloads.slice(i, i + MERGE_IMPORT_CHUNK_SIZE);
    const r = await mergeImportChannels({
      playlist_id: playlistId,
      channels: chunk,
      import_mode: importMode,
      ...attrFlags,
    });
    if (!r.success) {
      throw new Error(r.message || 'Merge import failed');
    }
    added += r.added ?? 0;
    skipped += r.skipped ?? 0;
    errors += r.errors ?? 0;
    parsed += r.parsed ?? chunk.length;
    onProgress?.(Math.min(i + chunk.length, total), total);
  }

  return { added, skipped, errors, parsed: parsed || total };
}

export type MergeImportM3uParams = {
  playlist_id: number;
  import_mode?: MergeImportMode;
  content?: string;
  url?: string;
  import_category?: boolean;
  import_image?: boolean;
  import_epg?: boolean;
  import_epg_shift?: boolean;
  import_channel_number?: boolean;
};

/** Server-side M3U parse + bulk insert (fast path for add-list wizard). */
export async function mergeImportM3u(params: MergeImportM3uParams): Promise<MergeImportResult> {
  return apiJson('/api/playlists/merge_import_m3u.php', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function mergeImportM3uFile(
  playlistId: number,
  file: File,
  opts: Omit<MergeImportM3uParams, 'playlist_id' | 'content' | 'url'> = {},
): Promise<MergeImportResult> {
  const fd = new FormData();
  fd.append('playlist_id', String(playlistId));
  fd.append('playlist_file', file);
  fd.append('import_mode', opts.import_mode ?? 'append_all');
  fd.append('import_category', opts.import_category !== false ? '1' : '0');
  fd.append('import_image', opts.import_image !== false ? '1' : '0');
  fd.append('import_epg', opts.import_epg !== false ? '1' : '0');
  fd.append('import_epg_shift', opts.import_epg_shift ? '1' : '0');
  fd.append('import_channel_number', opts.import_channel_number ? '1' : '0');
  return apiJsonFormData<MergeImportResult>('/api/playlists/merge_import_m3u.php', fd);
}

/** Multipart: playlist_name, playlist_file — bez Content-Type (ustawi przeglądarka). */
export async function uploadPlaylistM3u(formData: FormData): Promise<{
  success: boolean;
  playlist_id?: number;
  message?: string;
  added?: number;
  total?: number;
}> {
  const res = await apiFetch('/api/playlists/upload.php', { method: 'POST', body: formData });
  const text = await res.text();
  try {
    return JSON.parse(text) as { success: boolean; playlist_id?: number; message?: string };
  } catch {
    throw new Error(text.slice(0, 200));
  }
}

export async function downloadPlaylistM3uFile(playlistId: number): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/api/playlist/download.php?playlist_id=${encodeURIComponent(String(playlistId))}`);
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const disp = res.headers.get('Content-Disposition') || '';
  const match = disp.match(/filename="?([^"]+)"?/i);
  const filename = (match?.[1] || `playlist-${playlistId}.m3u`).trim();
  return { blob, filename };
}
